import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Search } from "lucide-react";
import { api } from "@/lib/api";

const OLD_TESTAMENT = [
    "Genesis", "Exodus", "Leviticus", "Numbers", "Deuteronomy",
    "Joshua", "Judges", "Ruth", "1 Samuel", "2 Samuel",
    "1 Kings", "2 Kings", "1 Chronicles", "2 Chronicles",
    "Ezra", "Nehemiah", "Esther", "Job", "Psalms",
    "Proverbs", "Ecclesiastes", "Song of Solomon",
    "Isaiah", "Jeremiah", "Lamentations", "Ezekiel", "Daniel",
    "Hosea", "Joel", "Amos", "Obadiah", "Jonah", "Micah",
    "Nahum", "Habakkuk", "Zephaniah", "Haggai", "Zechariah", "Malachi",
];

const NEW_TESTAMENT = [
    "Matthew", "Mark", "Luke", "John", "Acts",
    "Romans", "1 Corinthians", "2 Corinthians", "Galatians",
    "Ephesians", "Philippians", "Colossians",
    "1 Thessalonians", "2 Thessalonians", "1 Timothy", "2 Timothy",
    "Titus", "Philemon", "Hebrews", "James",
    "1 Peter", "2 Peter", "1 John", "2 John", "3 John", "Jude", "Revelation",
];

function BookCard({ name, count }) {
    const has = count > 0;
    return (
        <Link
            to={`/lessons?book=${encodeURIComponent(name)}`}
            className={`card-sticker p-4 transition-all hover:shadow-sticker-lg hover:-translate-x-[2px] hover:-translate-y-[2px] ${has ? "bg-white" : "bg-white opacity-60"}`}
            data-testid={`book-card-${name.replace(/\s/g, '-')}`}
        >
            <div className="flex items-start justify-between gap-2">
                <p className="font-heading font-semibold leading-tight">{name}</p>
                {has && (
                    <span className="pill bg-sun text-xs whitespace-nowrap">{count}</span>
                )}
            </div>
            <p className={`mt-2 text-xs ${has ? "text-coral font-bold" : "text-ink/50"}`}>
                {has ? `${count} lesson${count === 1 ? "" : "s"} →` : "Coming soon"}
            </p>
        </Link>
    );
}

export default function BibleNavigator() {
    const [counts, setCounts] = useState({});
    const [filter, setFilter] = useState("");

    useEffect(() => {
        api.get("/stats/books")
            .then((r) => {
                const map = {};
                (r.data.books || []).forEach((b) => {
                    map[(b.book || "").toLowerCase()] = b.count;
                });
                setCounts(map);
            })
            .catch(() => {});
    }, []);

    const filtered = useMemo(() => {
        const f = filter.trim().toLowerCase();
        if (!f) return { ot: OLD_TESTAMENT, nt: NEW_TESTAMENT };
        return {
            ot: OLD_TESTAMENT.filter((b) => b.toLowerCase().includes(f)),
            nt: NEW_TESTAMENT.filter((b) => b.toLowerCase().includes(f)),
        };
    }, [filter]);

    const totalBooks = OLD_TESTAMENT.length + NEW_TESTAMENT.length;
    const booksWithLessons = Object.keys(counts).length;

    return (
        <div data-testid="bible-page">
            <section className="border-b-2 border-ink bg-sky/30">
                <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12">
                    <span className="pill bg-white">
                        <BookOpen className="w-3.5 h-3.5" strokeWidth={2.5} /> Bible Navigator
                    </span>
                    <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mt-3">
                        All 66 Books of the Bible
                    </h1>
                    <p className="text-lg text-ink/80 mt-3 max-w-2xl">
                        Browse every book of the Old and New Testament. Tap a book with lessons to read all stories from that book of the ESV Bible.
                    </p>
                    <div className="mt-6 flex items-center gap-3">
                        <div className="relative flex-1 sm:flex-initial">
                            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-ink/50" strokeWidth={2.5} />
                            <input
                                type="text"
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                placeholder="Find a book..."
                                className="w-full sm:w-72 border-2 border-ink rounded-full bg-white pl-10 pr-4 py-2 text-sm font-semibold focus:outline-none focus:ring-4 focus:ring-sun"
                                data-testid="bible-filter-input"
                            />
                        </div>
                        <span className="text-sm text-ink/70">
                            <strong>{booksWithLessons}</strong> of <strong>{totalBooks}</strong> books have lessons
                        </span>
                    </div>
                </div>
            </section>

            <section className="max-w-6xl mx-auto px-5 sm:px-8 py-12">
                <h2 className="font-heading text-2xl font-bold mb-1 flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-sun border-2 border-ink"><BookOpen className="w-5 h-5" strokeWidth={2.5} /></span>
                    Old Testament
                </h2>
                <p className="text-ink/60 text-sm mb-5">39 books</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="ot-grid">
                    {filtered.ot.map((book) => (
                        <BookCard key={book} name={book} count={counts[book.toLowerCase()] || 0} />
                    ))}
                </div>
            </section>

            <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-16">
                <h2 className="font-heading text-2xl font-bold mb-1 flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-coral border-2 border-ink"><BookOpen className="w-5 h-5 text-white" strokeWidth={2.5} /></span>
                    New Testament
                </h2>
                <p className="text-ink/60 text-sm mb-5">27 books</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="nt-grid">
                    {filtered.nt.map((book) => (
                        <BookCard key={book} name={book} count={counts[book.toLowerCase()] || 0} />
                    ))}
                </div>
            </section>
        </div>
    );
}
